// English / Bahasa Indonesia. Static markup uses data-i18n (text) and data-i18n-aria (aria-label);
// code calls t(). The choice is saved; first visit follows the browser language.

export type Lang = 'en' | 'id';

type Entry = { en: string; id: string };

export const STRINGS = {
  // Static markup
  'loading': { en: 'Warming up the physics…', id: 'Memanaskan mesin fisika…' },
  'hud.best': { en: 'Best', id: 'Terbaik' },
  'hud.gas': { en: 'Gas', id: 'Bensin' },
  'hud.fuel': { en: 'Fuel', id: 'Bensin' },
  'hud.pause': { en: 'Pause', id: 'Jeda' },
  'hint.touch': { en: 'Hold <b>GAS</b> to drive', id: 'Tahan <b>GAS</b> untuk jalan' },
  'hint.desktop': { en: 'Hold <kbd>→</kbd> to drive · <kbd>←</kbd> brake', id: 'Tahan <kbd>→</kbd> untuk jalan · <kbd>←</kbd> rem' },
  'hint.air': { en: 'In the air: gas tilts back, brake tilts forward', id: 'Di udara: gas mendongak, rem menunduk' },
  'pedal.gas': { en: 'Gas', id: 'Gas' },
  'pedal.brake': { en: 'Brake', id: 'Rem' },
  'garage': { en: 'Garage', id: 'Garasi' },
  'map': { en: 'Map', id: 'Peta' },
  'paused': { en: 'Paused', id: 'Jeda' },
  'resume': { en: 'Resume', id: 'Lanjut' },
  'restart': { en: 'Restart run', id: 'Ulangi' },
  'goals.aria': { en: 'Missions and daily goals', id: 'Misi dan target harian' },
  'new-best': { en: 'New best!', id: 'Rekor baru!' },
  'stat.best': { en: 'Best', id: 'Terbaik' },
  'stat.coins': { en: 'Coins', id: 'Koin' },
  'stat.flips': { en: 'Flips', id: 'Salto' },
  'wallet': { en: 'Wallet', id: 'Dompet' },
  'retry': { en: 'Retry', id: 'Coba lagi' },
  'drive': { en: 'Drive', id: 'Jalan' },
  'back': { en: 'Back', id: 'Kembali' },
  // Dynamic text
  'cause.crash': { en: 'Flipped onto the roof', id: 'Terbalik di atap' },
  'cause.fuel': { en: 'Out of fuel', id: 'Bensin habis' },
  'sound.on': { en: 'Sound: On', id: 'Suara: Nyala' },
  'sound.off': { en: 'Sound: Off', id: 'Suara: Mati' },
  'ghost.on': { en: 'Ghost: On', id: 'Bayangan: Nyala' },
  'ghost.off': { en: 'Ghost: Off', id: 'Bayangan: Mati' },
  'lang.button': { en: 'Language: English', id: 'Bahasa: Indonesia' },
  'menu.tagline': { en: 'Deliver free school meals up every hill', id: 'Antar makan bergizi gratis ke setiap bukit' },
  'menu.play': { en: 'Play', id: 'Main' },
  'menu.route': { en: 'Route', id: 'Rute' },
  'menu.best': { en: 'Best {m} m', id: 'Terbaik {m} m' },
  'menu.no-best': { en: 'No run yet', id: 'Belum ada rekor' },
  'menu.driver': { en: 'Driver: {name}', id: 'Sopir: {name}' },
  'menu.set-name': { en: 'Set your driver name', id: 'Isi nama sopir' },
  'menu.home': { en: 'Main menu', id: 'Menu utama' },
  'menu.short': { en: 'Menu', id: 'Menu' },
  'name.title': { en: 'Driver name', id: 'Nama sopir' },
  'name.prompt': { en: 'Pick the name shown on the leaderboard. You can change it later.', id: 'Pilih nama yang tampil di papan skor. Bisa diganti nanti.' },
  'name.start': { en: 'Save and play', id: 'Simpan dan main' },
  'name.save': { en: 'Save', id: 'Simpan' },
  'board': { en: 'Leaderboard', id: 'Papan Skor' },
  'board.join': { en: 'Pick a name to join the leaderboard', id: 'Pilih nama untuk masuk papan skor' },
  'board.save': { en: 'Join', id: 'Gabung' },
  'board.bad-name': { en: 'Use 3–16 letters, digits, spaces or _ - .', id: 'Pakai 3–16 huruf, angka, spasi atau _ - .' },
  'board.race': { en: 'Race #1', id: 'Balap #1' },
  'board.racing': { en: 'Racing #1: {name}', id: 'Balapan dengan #1: {name}' },
  'board.rank': { en: 'Rank #{rank} of {total}', id: 'Peringkat #{rank} dari {total}' },
  'board.you': { en: 'you', id: 'kamu' },
  'board.empty': { en: 'No runs yet. Be the first!', id: 'Belum ada skor. Jadilah yang pertama!' },
  'board.offline': { en: 'Leaderboard offline', id: 'Papan skor sedang offline' },
  'board.loading': { en: 'Loading…', id: 'Memuat…' },
  'board.join-cta': { en: 'Join the leaderboard', id: 'Masuk papan skor' },
  'quality.high': { en: 'Graphics: High', id: 'Grafis: Tinggi' },
  'quality.low': { en: 'Graphics: Low', id: 'Grafis: Rendah' },
  'tip.fuel': { en: 'Watch the fuel gauge: grab the gas cans before it runs dry.', id: 'Perhatikan bensin: ambil jeriken sebelum habis.' },
  'tip.air': { en: 'In the air, gas tilts back and brake tilts forward. Land on both wheels!', id: 'Di udara, gas mendongak dan rem menunduk. Mendarat dengan dua roda!' },
  'tip.school': { en: 'Every school pays +20. Spend coins in the Garage.', id: 'Setiap sekolah membayar +20. Belanjakan koin di Garasi.' },
  'ghost.gap': { en: 'Ghost {gap} m', id: 'Bayangan {gap} m' },
  'ghost.label': { en: 'BEST {m} m', id: 'TERBAIK {m} m' },
  'toast.backflip': { en: 'Backflip +{coins}', id: 'Salto belakang +{coins}' },
  'toast.frontflip': { en: 'Frontflip +{coins}', id: 'Salto depan +{coins}' },
  'toast.fuel': { en: 'Fuel +{fuel}', id: 'Bensin +{fuel}' },
  'toast.air': { en: 'Air {s}s +{coins}', id: 'Melayang {s}d +{coins}' },
  'toast.gascan': { en: 'Gas can', id: 'Jeriken bensin' },
  'toast.delivered': { en: 'Delivered {m} m +{coins}', id: 'Terkirim {m} m +{coins}' },
  'toast.mission': { en: 'Mission: {text} +{coins}', id: 'Misi: {text} +{coins}' },
  'toast.daily': { en: 'Daily: {text} +{coins}', id: 'Harian: {text} +{coins}' },
  'toast.daily-all': { en: 'All daily goals +{coins}', id: 'Semua target harian +{coins}' },
  'goals.missions': { en: 'Missions', id: 'Misi' },
  'goals.today': { en: 'Today', id: 'Hari ini' },
  'goals.all-three': { en: 'All three today', id: 'Ketiganya hari ini' },
  'up.engine': { en: 'Engine', id: 'Mesin' },
  'up.tyres': { en: 'Tyres', id: 'Ban' },
  'up.suspension': { en: 'Suspension', id: 'Suspensi' },
  'up.tank': { en: 'Fuel tank', id: 'Tangki' },
  'up.stock-power': { en: 'Stock power', id: 'Tenaga standar' },
  'up.power': { en: 'Power {v}', id: 'Tenaga {v}' },
  'up.stock-grip': { en: 'Stock grip', id: 'Cengkeraman standar' },
  'up.grip': { en: 'Grip {v}', id: 'Cengkeraman {v}' },
  'up.stock-springs': { en: 'Stock springs', id: 'Per standar' },
  'up.travel': { en: 'Travel {v}', id: 'Langkah {v}' },
  'up.tank-l': { en: 'Tank {v} L', id: 'Tangki {v} L' },
  'up.max': { en: 'Max', id: 'Maks' },
  'up.level': { en: '{name} level', id: 'Level {name}' },
  'up.buy-aria': { en: 'Upgrade {name} for {cost} coins', id: 'Tingkatkan {name} seharga {cost} koin' },
  'up.max-aria': { en: '{name} is maxed', id: '{name} sudah maksimal' },
  'veh.driving': { en: 'Driving', id: 'Dipakai' },
  'veh.drive': { en: 'Drive', id: 'Pakai' },
  'veh.buy-aria': { en: 'Buy {name} for {cost} coins', id: 'Beli {name} seharga {cost} koin' },
  'map.driving': { en: 'Driving', id: 'Di sini' },
  'map.best': { en: 'Best {m} m', id: 'Terbaik {m} m' },
  'map.new': { en: 'New', id: 'Baru' },
  'map.reach': { en: 'Reach {m} m in {env}', id: 'Capai {m} m di {env}' },
  // Environment and vehicle blurbs
  'env.bukit-sawah': { en: 'Rice terraces under the volcano. Where every delivery starts.', id: 'Sawah terasering di kaki gunung. Tempat setiap pengantaran dimulai.' },
  'env.kebun-teh': { en: 'Misty tea-plantation highlands. Tight rollers and steep, damp climbs.', id: 'Kebun teh berkabut di dataran tinggi. Gelombang rapat dan tanjakan licin.' },
  'env.jalan-kampung': { en: 'Village lanes: concrete ramps, speed bumps and warung on every corner.', id: 'Jalan kampung: tanjakan beton, polisi tidur, dan warung di tiap sudut.' },
  'env.pantai-bali': { en: 'Soft sand, cliff-top kickers and long drops toward the surf.', id: 'Pasir lembut, lompatan di tebing, dan turunan panjang ke arah ombak.' },
  'env.kawah-bromo': { en: 'The volcanic ash sea at dawn. Loose sand, tall dunes, big drops.', id: 'Lautan pasir vulkanik saat fajar. Pasir gembur, bukit tinggi, turunan curam.' },
  'veh.pickup': { en: 'The all-rounder. Steady, forgiving, room for a full load of meal trays.', id: 'Serba bisa. Stabil, mudah dikendarai, muat banyak ompreng makanan.' },
  'veh.motor': { en: 'Light courier bike. Flips fast and sips fuel, but the rider has no roof.', id: 'Motor kurir ringan. Cepat bersalto dan irit, tapi pengendaranya tanpa atap.' },
  'veh.truck': { en: 'The kitchen truck. Heavy, slow and very hard to flip, with a huge tank.', id: 'Truk dapur. Berat, pelan, sulit terbalik, dengan tangki besar.' },
  // Missions and daily goals
  'mission.bukit-sawah:1': { en: 'Deliver to the first school (500 m)', id: 'Antar ke sekolah pertama (500 m)' },
  'mission.bukit-sawah:2': { en: 'Land a flip', id: 'Mendarat setelah salto' },
  'mission.bukit-sawah:3': { en: 'Collect 50 coins in one run', id: 'Kumpulkan 50 koin dalam satu putaran' },
  'mission.kebun-teh:1': { en: 'Reach 600 m', id: 'Capai 600 m' },
  'mission.kebun-teh:2': { en: 'Stay airborne for 2 s', id: 'Melayang selama 2 detik' },
  'mission.kebun-teh:3': { en: 'Deliver to 2 schools in one run', id: 'Antar ke 2 sekolah dalam satu putaran' },
  'mission.jalan-kampung:1': { en: 'Do 2 flips in one run', id: 'Lakukan 2 salto dalam satu putaran' },
  'mission.jalan-kampung:2': { en: 'Reach 700 m on the Motor Antar', id: 'Capai 700 m dengan Motor Antar' },
  'mission.jalan-kampung:3': { en: 'Collect 70 coins in one run', id: 'Kumpulkan 70 koin dalam satu putaran' },
  'mission.pantai-bali:1': { en: 'Stay airborne for 2.5 s', id: 'Melayang selama 2,5 detik' },
  'mission.pantai-bali:2': { en: 'Reach 1000 m', id: 'Capai 1000 m' },
  'mission.pantai-bali:3': { en: 'Do 3 flips in one run', id: 'Lakukan 3 salto dalam satu putaran' },
  'mission.kawah-bromo:1': { en: 'Reach 800 m in the Truk Dapur', id: 'Capai 800 m dengan Truk Dapur' },
  'mission.kawah-bromo:2': { en: 'Deliver to 3 schools in one run', id: 'Antar ke 3 sekolah dalam satu putaran' },
  'mission.kawah-bromo:3': { en: 'Stay airborne for 3 s', id: 'Melayang selama 3 detik' },
  'daily.drive-1500': { en: 'Drive 1500 m', id: 'Tempuh 1500 m' },
  'daily.drive-3000': { en: 'Drive 3000 m', id: 'Tempuh 3000 m' },
  'daily.coins-80': { en: 'Collect 80 coins', id: 'Kumpulkan 80 koin' },
  'daily.coins-150': { en: 'Collect 150 coins', id: 'Kumpulkan 150 koin' },
  'daily.flips-3': { en: 'Land 3 flips', id: 'Mendarat 3 salto' },
  'daily.flips-6': { en: 'Land 6 flips', id: 'Mendarat 6 salto' },
  'daily.deliver-3': { en: 'Deliver to 3 schools', id: 'Antar ke 3 sekolah' },
  'daily.runs-5': { en: 'Finish 5 runs', id: 'Selesaikan 5 putaran' },
} satisfies Record<string, Entry>;

export type StringKey = keyof typeof STRINGS;

const KEY = 'mbg-climb:lang';
let lang: Lang = initialLang();
const listeners: Array<() => void> = [];

function initialLang(): Lang {
  try {
    const saved = window.localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'id') return saved;
  } catch {
    // Fall through to the browser language.
  }
  const nav = typeof navigator !== 'undefined' ? navigator.languages?.[0] ?? navigator.language ?? '' : '';
  return nav.toLowerCase().startsWith('id') || nav.toLowerCase().startsWith('ms') ? 'id' : 'en';
}

export function getLang(): Lang {
  return lang;
}

export function setLang(next: Lang): void {
  lang = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    // Lasts for this session only.
  }
  applyStatic();
  for (const fn of listeners) fn();
}

/** Called after the language changes (components re-render their dynamic text). */
export function onLangChange(fn: () => void): void {
  listeners.push(fn);
}

/** Translated text with {placeholders} filled in. */
export function t(key: StringKey, params: Record<string, string | number> = {}): string {
  const entry: Entry = STRINGS[key];
  return entry[lang].replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`));
}

/** Text by a data id (missions, goals, blurbs) with an English fallback from the config. */
export function tId(key: string, fallback: string): string {
  const entry = (STRINGS as Record<string, Entry>)[key];
  return entry ? entry[lang] : fallback;
}

/** Applies data-i18n (innerHTML, trusted strings only), data-i18n-aria and the document language. */
export function applyStatic(root: ParentNode = document): void {
  document.documentElement.lang = lang;
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n]')) el.innerHTML = t(el.dataset.i18n as StringKey);
  for (const el of root.querySelectorAll<HTMLElement>('[data-i18n-aria]')) el.setAttribute('aria-label', t(el.dataset.i18nAria as StringKey));
}

/** Formats a decimal for the current language (Indonesian uses a comma). */
export function num(value: number, digits = 1): string {
  return value.toLocaleString(lang === 'id' ? 'id-ID' : 'en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
